import React, { useState } from "react";
import { FormControl, FormLabel, Input, FormHelperText, Box, LinearProgress, Typography } from "@mui/joy";
import { getPasswordStrength } from "../utils/validators";

interface PasswordStrengthProps {
  password: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  maxLength?: number;
}

/**
 * Password input with strength indicator
 */
export function PasswordStrengthInput({
  password,
  onChange,
  label = "Password",
  placeholder = "Enter password",
  error,
  disabled = false,
  maxLength = 128,
}: PasswordStrengthProps) {
  const { strength, percentage } = getPasswordStrength(password);

  const strengthColors: Record<"weak" | "fair" | "good" | "strong", any> = {
    weak: "danger",
    fair: "warning",
    good: "primary",
    strong: "success",
  };

  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <Input
        placeholder={placeholder}
        type="password"
        value={password}
        onChange={(e) => onChange(e.target.value.slice(0, maxLength))}
        disabled={disabled}
        slotProps={{
          input: {
            maxLength,
          },
        }}
        error={!!error}
      />

      {password && (
        <Box sx={{ mt: 1, gap: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
            <Typography level="body-xs">Password Strength:</Typography>
            <Typography level="body-xs" sx={{ color: `var(--joy-palette-${strengthColors[strength]}-500)` }}>
              {strength.charAt(0).toUpperCase() + strength.slice(1)}
            </Typography>
          </Box>
          <LinearProgress
            determinate
            value={percentage}
            sx={{
              color: strengthColors[strength],
              height: 6,
              borderRadius: 3,
            }}
          />
        </Box>
      )}

      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
}
